import Sensors from '@mui/icons-material/Sensors'
import SensorsOff from '@mui/icons-material/SensorsOff'
import {
    Alert,
    Badge,
    Box,
    IconButton,
    Link,
    Popover,
    SxProps,
    Theme,
    Tooltip,
} from '@mui/material'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import VersionWarningPopup from './VersionWarningPopup'
import ReportIcon from '@mui/icons-material/Report'
import HelpIcon from '@mui/icons-material/Help'
import { useTranslation } from 'react-i18next'
import {
    usePOSCFDHubContext,
    usePrinterHubContext,
} from 'hooks/usePOSHubContext/usePOSHubContext'
import DesktopAccessDisabledIcon from '@mui/icons-material/DesktopAccessDisabled'
import PrintDisabledIcon from '@mui/icons-material/PrintDisabled'
import { useHistory } from 'react-router-dom'
import useDefaultDevice from 'hooks/useDefaultDevice/useDefaultDevice'
const isLocal = (url: string) =>
    url.includes('localhost') || url.includes('127.0.0.1')

const warningIcon: SxProps<Theme> = {
    position: 'absolute',
    top: -5,
    right: 0,
}

const sensorIcon: SxProps<Theme> = {
    fontSize: '28px',
}
function NoConnectionPopup({
    open,
    onClose,
    anchorEl,
    children,
}: {
    anchorEl: any
    onClose: () => void
    open: boolean
    children: ReactNode
}) {
    return (
        <Popover
            sx={{ boxShadow: 'none' }}
            onClose={onClose}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            {children}
        </Popover>
    )
}
function HWAgentStatusIcon() {
    const { t } = useTranslation()
    const { printingHardwareAgentUri, cfdHardwareAgentUri } = useDefaultDevice()

    const {
        isOnline: isPrinterHwAgentOnline,
        isUncompatibleVersion: isPrinterUncompatibleVersion,
        isOlderLatestVersion: isPrinterOlderLatestVersion,
        version: printerVersion,
        compatibilityVersion: printerCompatibilityVersion,
        latestVersion: printerLatestVersion,
    } = usePrinterHubContext()

    const {
        useCFD,
        isHwAgentOnline: isCFDHwAgentOnline,
        isUncompatibleVersion: isCFDUncompatibleVersion,
        isOlderLatestVersion: isCFDOlderLatestVersion,
        version: cfdVersion,
        compatibilityVersion: cfdCompatibilityVersion,
        latestVersion: cfdLatestVersion,
    } = usePOSCFDHubContext()

    const history = useHistory()
    const redirectTo = useCallback(
        (path: string) => {
            history.push(path)
        },
        [history],
    )

    const [openVersionWarning, setOpenVersionWarning] = useState<boolean>(false)
    const [buttonRef, setButtonRef] = useState<Element | null>()

    const onOpenVersionWarning = useCallback((e: any) => {
        setOpenVersionWarning(true)
        setButtonRef(e.currentTarget)
    }, [])

    const onCloseVersionWarning = useCallback(() => {
        setOpenVersionWarning(false)
        setButtonRef(null)
    }, [])

    const isWarningVersion = useMemo(
        () =>
            isPrinterUncompatibleVersion ||
            isPrinterOlderLatestVersion ||
            isCFDUncompatibleVersion ||
            isCFDOlderLatestVersion,
        [
            isPrinterUncompatibleVersion,
            isPrinterOlderLatestVersion,
            isCFDUncompatibleVersion,
            isCFDOlderLatestVersion,
        ],
    )
    const warningData = useMemo(() => {
        const isSameUri =
            printingHardwareAgentUri === cfdHardwareAgentUri ||
            (isLocal(printingHardwareAgentUri) && isLocal(cfdHardwareAgentUri))
        if (isSameUri)
            return {
                type: '',
                isUncompatibleVersion: isPrinterUncompatibleVersion,
                isOlderLatestVersion: isPrinterOlderLatestVersion,
                version: printerVersion,
                compatibilityVersion: printerCompatibilityVersion,
                latestVersion: printerLatestVersion,
            }

        return isPrinterUncompatibleVersion || isPrinterOlderLatestVersion
            ? {
                  type: 'printer',
                  isUncompatibleVersion: isPrinterUncompatibleVersion,
                  isOlderLatestVersion: isPrinterOlderLatestVersion,
                  version: printerVersion,
                  compatibilityVersion: printerCompatibilityVersion,
                  latestVersion: printerLatestVersion,
              }
            : {
                  type: 'CFD',
                  isUncompatibleVersion: isCFDUncompatibleVersion,
                  isOlderLatestVersion: isCFDOlderLatestVersion,
                  version: cfdVersion,
                  compatibilityVersion: cfdCompatibilityVersion,
                  latestVersion: cfdLatestVersion,
              }
    }, [
        cfdCompatibilityVersion,
        cfdHardwareAgentUri,
        cfdLatestVersion,
        cfdVersion,
        printerCompatibilityVersion,
        isCFDOlderLatestVersion,
        isCFDUncompatibleVersion,
        isPrinterOlderLatestVersion,
        isPrinterUncompatibleVersion,
        printerLatestVersion,
        printingHardwareAgentUri,
        printerVersion,
    ])

    return isPrinterHwAgentOnline || isCFDHwAgentOnline ? (
        <Box display="flex" alignItems="center">
            {!isCFDHwAgentOnline && useCFD ? (
                <>
                    <IconButton onClick={onOpenVersionWarning} color="inherit">
                        <Badge
                            badgeContent={
                                <DesktopAccessDisabledIcon sx={{ width: 12 }} />
                            }
                            color="error"
                        >
                            <Sensors sx={sensorIcon} />
                        </Badge>
                    </IconButton>
                    <NoConnectionPopup
                        anchorEl={buttonRef}
                        open={openVersionWarning}
                        onClose={onCloseVersionWarning}
                    >
                        <Alert
                            icon={<DesktopAccessDisabledIcon />}
                            severity="error"
                        >
                            <Box ml={1}>
                                Unable to connect the CFD Hardware agent.
                            </Box>
                            Go to{' '}
                            <Link
                                color={'inherit'}
                                onClick={() =>
                                    redirectTo(
                                        '/terminal-preferences/customer-facing-display',
                                    )
                                }
                            >
                                setting
                            </Link>
                        </Alert>
                    </NoConnectionPopup>
                </>
            ) : !isPrinterHwAgentOnline ? (
                <>
                    <IconButton onClick={onOpenVersionWarning} color="inherit">
                        <Badge
                            badgeContent={
                                <PrintDisabledIcon sx={{ width: 14 }} />
                            }
                            color="error"
                        >
                            <Sensors sx={sensorIcon} />
                        </Badge>
                    </IconButton>
                    <NoConnectionPopup
                        anchorEl={buttonRef}
                        open={openVersionWarning}
                        onClose={onCloseVersionWarning}
                    >
                        <Alert icon={<PrintDisabledIcon />} severity="error">
                            <Box ml={1}>
                                Unable to connect the Printer Hardware agent.
                            </Box>
                            Go to{' '}
                            <Link
                                color={'inherit'}
                                onClick={() =>
                                    redirectTo('/terminal-preferences/printing')
                                }
                            >
                                setting
                            </Link>
                        </Alert>
                    </NoConnectionPopup>
                </>
            ) : isWarningVersion ? (
                <>
                    <IconButton color="inherit" onClick={onOpenVersionWarning}>
                        {isPrinterUncompatibleVersion ||
                        isCFDUncompatibleVersion ? (
                            <ReportIcon color="warning" sx={warningIcon} />
                        ) : isPrinterOlderLatestVersion ||
                          isCFDOlderLatestVersion ? (
                            <HelpIcon color="info" sx={warningIcon} />
                        ) : (
                            <></>
                        )}
                        <Sensors sx={sensorIcon} />
                    </IconButton>
                    <VersionWarningPopup
                        anchorEl={buttonRef}
                        open={openVersionWarning}
                        onClose={onCloseVersionWarning}
                        {...warningData}
                    />
                </>
            ) : (
                <Sensors sx={sensorIcon} />
            )}
        </Box>
    ) : (
        <Tooltip title={t('hwAgent.cannot_find_hwagent_run_on_your_system')}>
            <SensorsOff sx={sensorIcon} />
        </Tooltip>
    )
}

export default HWAgentStatusIcon
